import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Mail, AlertCircle, CheckCircle2 } from "lucide-react";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type FormData = z.infer<typeof formSchema>;

interface RequestDemoFormProps {
  onSuccess?: () => void;
}

const RequestDemoForm: React.FC<RequestDemoFormProps> = ({ onSuccess }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string>();
  const [isSuccess, setIsSuccess] = React.useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      setError(undefined);
      setIsSuccess(false);

      const response = await ApiService.request(
        EndpointKeys.REQUEST_DEMO_ACCOUNTS,
        {
          method: "POST",
          body: {
            email: data.email,
          },
          skipAuth: true,
        },
        false
      );

      setIsSuccess(true);
      form.reset();
      
      toast({
        title: "Success",
        description: "Demo accounts created! Check your email for login details.",
      });
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to request demo accounts";
      setError(message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-center">Get Your Demo Access</h2>
        <p className="text-muted-foreground text-center">
          We'll send you login details for both an employer and nanny account
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="animate-in fade-in-50 slide-in-from-top-5">
          <AlertCircle className="h-4 w-4 mr-2" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      {isSuccess && (
        <Alert className="bg-green-50 text-green-800 border-green-200 animate-in fade-in-50 slide-in-from-top-5">
          <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
          <AlertDescription>
            <span className="font-medium">Success!</span> Demo accounts created. Please check your email for login details.
          </AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base">Email Address</FormLabel>
                <FormControl>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
                      <Mail className="h-5 w-5" />
                    </div>
                    <Input
                      {...field}
                      type="email"
                      placeholder="you@example.com"
                      disabled={isLoading}
                      className="pl-10"
                    />
                  </div>
                </FormControl>
                <FormDescription>
                  We'll email you login details for both accounts to try
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full text-base font-medium py-6" 
            size="lg"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Accounts...
              </>
            ) : (
              "Get Demo Access Now"
            )}
          </Button>
          
          <div className="text-center text-sm text-muted-foreground">
            <p>No credit card required. Instant access.</p>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default RequestDemoForm; 